import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

export const Grid = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'minColumn',
})<{ minColumn: number }>(({ theme, minColumn }) => ({
  display: 'grid',
  gridTemplateColumns: `repeat(auto-fill, minmax(${minColumn}px, 1fr))`,
  columnGap: theme.palette.tokens.space[6],
  rowGap: theme.palette.tokens.space[4],
}));

export const FieldRoot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'span',
})<{ span?: number }>(({ span }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  minWidth: 0,
  ...(span && { gridColumn: `span ${span}` }),
  ...(span && span >= 99 && { gridColumn: '1 / -1' }),
}));

export const FieldLabel = styled(Typography)(({ theme }) => ({
  ...theme.typography.caption,
  color: theme.palette.text.secondary,
}));

interface FieldValueProps {
  isEmpty: boolean;
  emphasis: boolean;
  mono: boolean;
}

export const FieldValue = styled(Typography, {
  shouldForwardProp: (prop) =>
    !['isEmpty', 'emphasis', 'mono'].includes(String(prop)),
})<FieldValueProps>(({ theme, isEmpty, emphasis, mono }) => {
  const { tokens } = theme.palette;
  return {
    ...(emphasis ? theme.typography.body1 : theme.typography.body2),
    fontWeight: emphasis ? tokens.font.weightMedium : tokens.font.weightRegular,
    color: isEmpty ? theme.palette.text.disabled : theme.palette.text.primary,
    overflowWrap: 'anywhere',
    ...(mono && {
      fontVariantNumeric: 'tabular-nums lining-nums',
      fontFeatureSettings: '"tnum" 1, "lnum" 1',
    }),
  };
});

export const LongText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isEmpty',
})<{ isEmpty: boolean }>(({ theme, isEmpty }) => {
  const { tokens } = theme.palette;
  return {
    ...theme.typography.body2,
    whiteSpace: 'pre-wrap',
    padding: `${tokens.space[3]}px ${tokens.space[4]}px`,
    borderRadius: `${tokens.radius.control}px`,
    backgroundColor: tokens.color.surfaceHover,
    border: `1px solid ${theme.palette.divider}`,
    color: isEmpty ? theme.palette.text.disabled : theme.palette.text.primary,
    minHeight: '44px',
  };
});

export const BooleanChip = styled('span', {
  shouldForwardProp: (prop) => prop !== 'checked',
})<{ checked: boolean }>(({ theme, checked }) => {
  const { color, radius, font, type } = theme.palette.tokens;
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    alignSelf: 'flex-start',
    height: '22px',
    padding: '0 8px 0 4px',
    borderRadius: `${radius.pill}px`,
    fontSize: `${type.caption.size}px`,
    fontWeight: font.weightMedium,
    color: checked ? color.positive : color.textSecondary,
    backgroundColor: checked ? color.positiveTint : color.n100,
    '& span': { fontSize: '16px' },
  };
});

export const GroupRoot = styled(Box)(({ theme }) => {
  const { tokens } = theme.palette;
  return {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.space[4],
    padding: tokens.space[5],
    borderRadius: `${tokens.radius.card}px`,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    minWidth: 0,
  };
});

export const GroupTitle = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.palette.tokens.space[3],
  ...theme.typography.subtitle2,
  fontWeight: theme.palette.tokens.font.weightMedium,
  color: theme.palette.text.primary,
}));

export const GroupBody = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.palette.tokens.space[4],
}));

export const GroupGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
  gap: theme.palette.tokens.space[4],
  alignItems: 'start',
}));
